import {
  type AccordionDetailsProps,
  type AccordionSummaryProps,
  Accordion as MuiAccordion,
  AccordionDetails as MuiAccordionDetails,
  type AccordionProps as MuiAccordionProps,
  AccordionSummary as MuiAccordionSummary,
  type SxProps,
} from '@mui/material';
import { SPACING } from '@ovComponents/0-tokens/layout';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { useAccordionTokens } from './accordian.tokens';

interface AccordionProps extends Omit<MuiAccordionProps, 'variant'> {
  multiple?: boolean;
  fullWidthOnMobile?: boolean;
  variant?: MuiAccordionProps['variant'] | 'workflow';
  state?: 'error';
}

export const Accordion = ({
  multiple = false,
  fullWidthOnMobile = false,
  state,
  ...props
}: AccordionProps) => {
  const tokens = useThemeTokens(useAccordionTokens());
  const { comp } = tokens;

  const defaultAccordionSx: SxProps = {
    '&.MuiAccordion-root': {
      backgroundColor:
        state === 'error' ? `${tokens.ref.palette.negative20}` : undefined,
      border:
        state === 'error'
          ? `1px solid ${tokens.ref.palette.negative40}`
          : props.variant === 'elevation'
            ? 'none'
            : `1px solid ${comp.accordion.borderColor}`,
      marginLeft: { xs: fullWidthOnMobile ? '-16px' : 0, sm: 0 },
      marginRight: { xs: fullWidthOnMobile ? '-16px' : 0, sm: 0 },
      borderRadius: {
        xs: fullWidthOnMobile ? 0 : comp.accordion.borderRadius,
        sm: !multiple ? comp.accordion.borderRadius : 0,
      },
      marginBottom: props.variant === 'elevation' ? '' : `${1 * SPACING}px`,
      boxShadow: 'unset',
      '&:first-of-type': {
        borderRadius: {
          xs: fullWidthOnMobile
            ? 0
            : `${comp.accordion.borderRadius} ${comp.accordion.borderRadius} 0 0`,
          sm: `${comp.accordion.borderRadius} ${comp.accordion.borderRadius} 0 0`,
        },
        '.MuiAccordionSummary-root': {
          borderTop:
            props.variant === 'elevation' && multiple
              ? 'none'
              : `1px solid ${comp.accordion.multipleBorderColor}`,
        },
      },
      '&:last-of-type': {
        borderRadius: {
          xs: fullWidthOnMobile
            ? 0
            : `0 0 ${comp.accordion.borderRadius} ${comp.accordion.borderRadius}`,
          sm: `0 0 ${comp.accordion.borderRadius} ${comp.accordion.borderRadius}`,
        },
        '.MuiCollapse-root': {
          borderRadius: `0 0 ${comp.accordion.borderRadius} ${comp.accordion.borderRadius}`,
        },
      },
      '&:last-of-type&:first-of-type': {
        borderRadius: {
          xs: fullWidthOnMobile ? 0 : comp.accordion.borderRadius,
          sm: comp.accordion.borderRadius,
        },
        '.MuiCollapse-root': {
          borderRadius: comp.accordion.borderRadius,
        },
      },
      '.MuiAccordionSummary-root': {
        borderTop:
          props.variant === 'elevation' && multiple
            ? `1px solid ${comp.accordion.multipleBorderColor}`
            : 'none',
      },
    },
  };

  const workflowSxOverrides = {
    '&:before': {
      display: 'none',
      border: 'none',
    },
    '&.MuiAccordion-root': {
      border:
        state === 'error'
          ? `1px solid ${tokens.ref.palette.negative40}`
          : `1px solid ${comp.accordion.multipleBorderColor}`,
    },
    '&:last-of-type': {
      borderRadius: `${comp.accordion.borderRadius} !important`,
      '.MuiCollapse-root': {
        borderRadius: `${comp.accordion.borderRadius} !important`,
      },
    },
    '&:last-of-type&:first-of-type': {
      borderRadius: `${comp.accordion.borderRadius} !important`,
      '.MuiCollapse-root': {
        borderRadius: `${comp.accordion.borderRadius} !important`,
      },
    },
    '&:first-of-type': {
      borderRadius: `${comp.accordion.borderRadius} !important`,
      '.MuiCollapse-root': {
        borderRadius: `${comp.accordion.borderRadius} !important`,
      },
    },
  };

  const mergedProps = {
    ...props,
    ...{
      sx: [
        { ...defaultAccordionSx },
        ...(props.variant === 'workflow' ? [workflowSxOverrides] : []),
        ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
      ],
    },
    disableGutters: true,
  };
  return <MuiAccordion {...(mergedProps as MuiAccordionProps)} />;
};
export const AccordionDetails = (props: AccordionDetailsProps) => (
  <MuiAccordionDetails {...props} />
);
export const AccordionSummary = (props: AccordionSummaryProps) => (
  <MuiAccordionSummary {...props} />
);
